/**
 * Offer and answer between two clients that may both speak first.
 *
 * "Perfect negotiation", as the WebRTC specification names it: each side knows
 * ahead of time which of the two gives way, so two offers that cross on the
 * wire settle into one connection instead of two half-open ones. The rule is
 * the same on every relaii client (calls-v1 §7.4): the larger user id is
 * polite — it rolls its own offer back and answers — and the smaller one
 * ignores the offer that crossed its own.
 *
 * Every signal for a peer is handled in the order it arrived, one at a time.
 * WebRTC's calls are asynchronous, and an answer applied while the offer it
 * answers is still being set is refused; the queue is what makes the order on
 * the wire the order in the connection.
 */

import type { UUID } from '@/api/types'

/** Whether this side gives way when offers cross. Strings, not numbers. */
export function isPolite(selfId: UUID, remoteId: UUID): boolean {
  return selfId > remoteId
}

/** Runs each peer's work strictly in order; peers never wait for each other. */
export class SignalQueue {
  private readonly tails = new Map<UUID, Promise<void>>()

  /**
   * Runs `task` after everything already queued for the peer. The promise
   * settles with the task; a task that fails does not hold up the ones behind
   * it — one bad candidate is not the call.
   */
  run(peerId: UUID, task: () => Promise<void>): Promise<void> {
    const previous = this.tails.get(peerId) ?? Promise.resolve()
    const current = previous.then(task)
    const tail = current.catch(() => undefined)
    this.tails.set(peerId, tail)
    void tail.then(() => {
      if (this.tails.get(peerId) === tail) this.tails.delete(peerId)
    })
    return current
  }
}

/** The part of RTCPeerConnection negotiation touches, so a fake can stand in. */
export interface NegotiatingPeer {
  readonly signalingState: RTCSignalingState
  readonly localDescription: RTCSessionDescriptionInit | null
  readonly remoteDescription: RTCSessionDescriptionInit | null
  createOffer(options?: RTCOfferOptions): Promise<RTCSessionDescriptionInit>
  createAnswer(): Promise<RTCSessionDescriptionInit>
  setLocalDescription(description: RTCSessionDescriptionInit): Promise<void>
  setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void>
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void>
}

export type DescriptionSender = (kind: 'offer' | 'answer', payload: { sdp: RTCSessionDescriptionInit }) => void

/** An `offer` or `answer` signal's payload, as far as it can be trusted. */
export function isDescriptionPayload(value: object): value is { sdp: RTCSessionDescriptionInit } {
  const sdp = (value as { sdp?: unknown }).sdp
  return typeof sdp === 'object' && sdp !== null && typeof (sdp as { type?: unknown }).type === 'string'
}

/**
 * One connection's side of the negotiation. Every method is meant to be run
 * through the peer's `SignalQueue`, so none of them overlaps another.
 */
export class PeerNegotiator {
  /** The last offer received crossed ours and was ignored (impolite side). */
  private ignoreOffer = false
  /** An offer was wanted while another was outstanding; send it once settled. */
  private offerWanted = false
  private readonly held: RTCIceCandidateInit[] = []

  constructor(
    private readonly peer: NegotiatingPeer,
    private readonly polite: boolean,
    private readonly send: DescriptionSender,
  ) {}

  /**
   * Offers what the connection now carries. While an offer is already out
   * waiting for its answer, this one is remembered, not dropped: a screen
   * track added mid-negotiation still has to be offered.
   */
  async offer(restart = false): Promise<void> {
    if (this.peer.signalingState !== 'stable') {
      this.offerWanted = true
      return
    }
    this.offerWanted = false
    const offer = await this.peer.createOffer(restart ? { iceRestart: true } : undefined)
    await this.peer.setLocalDescription(offer)
    this.send('offer', { sdp: this.peer.localDescription ?? offer })
  }

  /**
   * A connection that has not come up: new ICE credentials, and a new offer
   * even when the last one was never answered — thirty seconds of silence is
   * not an answer on its way.
   */
  async restart(): Promise<void> {
    if (this.peer.signalingState === 'have-local-offer') {
      await this.peer.setLocalDescription({ type: 'rollback' })
    }
    await this.offer(true)
  }

  async receiveDescription(description: RTCSessionDescriptionInit): Promise<void> {
    const collision = description.type === 'offer' && this.peer.signalingState !== 'stable'
    this.ignoreOffer = !this.polite && collision
    if (this.ignoreOffer) return

    if (collision) {
      await this.peer.setLocalDescription({ type: 'rollback' })
      // Our offer is gone; whatever it was for still needs offering.
      this.offerWanted = true
    }
    await this.peer.setRemoteDescription(description)
    // One held candidate the connection will not take is not a reason to
    // leave the description unanswered or a wanted offer unsent.
    for (const candidate of this.held.splice(0)) {
      await this.addCandidate(candidate).catch((caught: unknown) => {
        console.warn('held ICE candidate refused', caught)
      })
    }

    if (description.type === 'offer') {
      const answer = await this.peer.createAnswer()
      await this.peer.setLocalDescription(answer)
      this.send('answer', { sdp: this.peer.localDescription ?? answer })
    }

    if (this.offerWanted && this.peer.signalingState === 'stable') await this.offer()
  }

  /** Candidates race the description they belong to; early ones are held. */
  async receiveCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (this.peer.remoteDescription === null) {
      // While an offer is being ignored the candidate is that offer's. Held,
      // it would be added under the next description, which it is not for.
      if (!this.ignoreOffer) this.held.push(candidate)
      return
    }
    await this.addCandidate(candidate)
  }

  private async addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await this.peer.addIceCandidate(candidate)
    } catch (caught) {
      // Candidates for an offer we chose to ignore are expected to fail.
      if (!this.ignoreOffer) throw caught
    }
  }
}
